import {
  Badge,
  Box,
  Center,
  Collapse,
  Flex,
  Icon,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDown, ChevronUp, ShieldAlert } from "lucide-react";
import React from "react";

import { EvidenceDetails } from "./EvidenceDetails";
import { type IntegritySignalCardData } from "./types";
import { parseEvidence, SEVERITY_CONFIG, SIGNAL_CONFIG } from "./utils";

export type { IntegritySignalCardData } from "./types";
export type IntegritySignalCardProps = {
  signal: IntegritySignalCardData;
  interviewStartTime?: string | null;
  interviewDuration?: number | null;
  onSeek?(t: number): void;
};

const DETECTED_CONFIG = {
  label: "Detected",
  colorScheme: "orange",
  iconBg: "orange.100",
  iconColor: "orange.700",
};

export const IntegritySignalCard: React.FC<IntegritySignalCardProps> = ({
  signal,
  interviewStartTime,
  interviewDuration,
  onSeek,
}) => {
  const { isOpen, onToggle } = useDisclosure();
  const config = SIGNAL_CONFIG[signal.signalType] ?? {
    label: signal.signalType,
    icon: ShieldAlert,
  };
  const severity = signal.detected ? DETECTED_CONFIG : SEVERITY_CONFIG.NONE;
  const signalEvidence = parseEvidence(signal.signalType, signal.evidence);

  return (
    <Box
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      overflow="hidden"
    >
      <Tooltip
        label={isOpen ? "Hide Details" : "See Details"}
        placement="top"
        hasArrow
      >
        <Flex
          as="button"
          w="100%"
          align="center"
          px="4"
          py="3"
          cursor="pointer"
          onClick={onToggle}
          _hover={{ bg: "white" }}
        >
          <Center
            bg={severity.iconBg}
            borderRadius="full"
            boxSize="8"
            mr="3"
            flexShrink={0}
          >
            <Icon as={config.icon} boxSize="4" color={severity.iconColor} />
          </Center>
          <Text fontWeight="500" fontSize="sm" flex="1" textAlign="left">
            {config.label}
          </Text>
          <Badge colorScheme={severity.colorScheme} fontSize="xs" mr="3">
            {severity.label}
          </Badge>
          <Icon
            as={isOpen ? ChevronUp : ChevronDown}
            boxSize="4"
            color="gray.400"
          />
        </Flex>
      </Tooltip>
      <Collapse in={isOpen}>
        <Box px="4" pb="3" pt="1" bg="white" fontSize="sm" color="gray.600">
          <EvidenceDetails
            signalEvidence={signalEvidence}
            interviewStartTime={interviewStartTime}
            interviewDuration={interviewDuration}
            onSeek={onSeek}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

export default IntegritySignalCard;
